import {
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import SocialSummary from "@/components/projects/social/Summary";
import { useState } from "react";
import SocialProductSelector from "@/components/projects/social/ProductSelector.tsx";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSelector } from "react-redux";
import type { Product } from "@commercial-publishing/shared-types/social";
import type { Maybe } from "@commercial-publishing/shared-types";

interface Props {
  socialProducts: Product[];
  onProductsChange: (socialProducts: Product[]) => void;
}

const ProductList = ({ socialProducts, onProductsChange }: Props) => {
  // TODO: make state type safe
  const instagramAccounts = useSelector(
    (state: any) => state.instagramAccounts,
  );

  // Index of the product currently being edited; null means we are adding a new one
  const [editingIndex, setEditingIndex] = useState<Maybe<number>>(undefined);
  const [showSelector, setShowSelector] = useState(false);

  const onEdit = (index: number) => {
    setEditingIndex(index);
    setShowSelector(true);
  };

  const onRemove = (index: number) => {
    const newSocialProducts = [...socialProducts];
    newSocialProducts.splice(index, 1);
    onProductsChange(newSocialProducts);
    setEditingIndex(undefined); // exit edit mode
    setShowSelector(false);
  };

  const onAdd = (product: Product) => {
    const newSocialProducts = [...socialProducts, product];
    onProductsChange(newSocialProducts);
    setEditingIndex(undefined); // exit edit mode
    setShowSelector(false);
  };

  const onUpdate = (product: Product) => {
    const updated = [...socialProducts];
    if (editingIndex === undefined) {
      throw new Error("editingIndex should not be null");
    }
    updated[editingIndex] = product;
    onProductsChange(updated);
    setEditingIndex(undefined); // exit edit mode
    setShowSelector(false);
  };
  return (
    <Grid container spacing={3} sx={{ maxWidth: 1200, mx: "auto" }}>
      <Grid item>
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontWeight: "bold", mb: 3 }}
        >
          Social Media Products
        </Typography>
      </Grid>
      {showSelector && (
        <Grid item>
          <SocialProductSelector
            // If we are editing, provide the existing product as the initial selection
            initialSelection={
              editingIndex !== undefined
                ? socialProducts[editingIndex]
                : undefined
            }
            instaOptions={instagramAccounts}
            onSave={(e) => {
              if (editingIndex !== undefined) {
                onUpdate(e);
              } else {
                onAdd(e);
              }
            }}
            onCancel={() => {
              setEditingIndex(undefined);
              setShowSelector(false);
            }}
          />
        </Grid>
      )}
      <Grid item container spacing={2}>
        {socialProducts.map((product, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Box sx={{ position: "relative" }}>
              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  zIndex: 1,
                }}
              >
                <IconButton
                  onClick={() => onEdit(index)}
                  size="small"
                  sx={{
                    backgroundColor: "background.paper",
                    boxShadow: 1,
                    "&:hover": {
                      backgroundColor: "primary.main",
                      color: "primary.contrastText",
                    },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={() => onRemove(index)}
                  size="small"
                  sx={{
                    backgroundColor: "background.paper",
                    boxShadow: 1,
                    "&:hover": {
                      backgroundColor: "error.main",
                      color: "error.contrastText",
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
              <SocialSummary {...product} />
            </Box>
          </Grid>
        ))}
      </Grid>
      {!showSelector && (
        <Grid item xs={12} sx={{ textAlign: "center" }}>
          <Button
            variant="outlined"
            onClick={() => setShowSelector(true)}
            style={{
              textTransform: "none",
              color: "#223647",
              minHeight: "47px",
              minWidth: "163px",
              borderColor: "#223647",
              fontSize: "16px",
              fontWeight: "700",
            }}
          >
            Add Another Product
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

export default ProductList;
